import { defineStore } from 'pinia';
import api from '🔗/api';
import { createEmitter } from '🔗/utils/utils';
import { generateSilentWav } from '🔗/utils/audio';

import { createPaneStore } from './pane'


export const usePlayerStore = defineStore('player', {
    state: () => ({
        autofade: true,
        volume: 0.5,
        mute: false,
        rate: 1,
        snippedMode: false,

        panes: {},
        _audioContext: null,

        _formats: ['ogg', 'wav']
    }),

    getters: {
        release: (state) => ({
            play: state.playRelease,
            pause: state.pauseRelease
        }),
        playlist: (state) => ({
            play: state.playPlaylist,
            pause: state.pausePlaylist
        }),
        dict: (state) => ({
            play: state.playDict,
            pause: state.pauseDict
        }),
        artist: (state) => ({
            play: state.playArtist,
            pause: state.pauseArtist
        }),
        queue: (state) => ({
            create: state.trackQueue,
            same: state.sameQueue,
            shuffle: state.shuffleQueue,
            removeTrack: state.removeFromQueue,
            clear: state.clearQueue
        })
    },

    actions: {
        // Artist
            async playArtist(id, options) {
                this.tracks({
                    params: {
                        contributors: id,
                        state: 'PUBLISHED'
                    },
                    sort: {
                        code: 'ratingTotal',
                        direction: 'desc'
                    },
                    loadParent: async () => await api.artists().get(id),
                    options: { type: 'artist', id },
                    autoPlay: true,
                    shuffle: !!options?.shuffle
                });
            },

            pauseArtist(id, pane = 'default') {
                this.pause(pane);
            },

        // Dist
            async playDict(code, type, options) {
                const dict = await api.dict()[[type]].get(code);

                this.tracks({
                    params: {
                        dicts: dict.id,
                        state: 'PUBLISHED'
                    },
                    sort: {
                        code: 'ratingTotal',
                        direction: 'desc'
                    },
                    loadParent: async () => dict,
                    options: { type: 'dict', id: dict.id, dict },
                    autoPlay: true,
                    shuffle: !!options?.shuffle
                });
            },

            pauseDict(code, pane = 'default') {
                this.pause(pane);
            },

        // Playlist
            async playPlaylist(id, options) {
                this.tracks({
                    options: { type: 'playlist', id },
                    load: async () => await api.playlists().tracks.list(id) || [],
                    loadParent: async () => await api.playlists().get(id),
                    autoPlay: true,
                    shuffle: !!options?.shuffle
                });
            },

            pausePlaylist(id, pane = 'default') {
                this.pause(pane);
            },

        // Release
            async playRelease(id, options) {
                this.tracks({
                    options: { type: 'release', id },
                    load: async () => await api.releases().tracks.list(id) || [],
                    loadParent: async () => await api.releases().get(id),
                    autoPlay: true,
                    shuffle: !!options?.shuffle
                });
            },

            pauseRelease(id, pane = 'default') {
                this.pause(pane);
            },

        // Audio Context
            async createAudioContext() {
                if (!this._audioContext) {
                    // Ctx
                    const Ctx = window.AudioContext || window.webkitAudioContext;
                    this._audioContext = new Ctx();

                    // // Ring Buffer Initialization
                    // const workletUrl = new URL('../worklets/ring-buffer/ring-buffer-worklet.js', import.meta.url).href;
                    // await this._audioContext.audioWorklet.addModule(workletUrl);

                    // // Native audio tap
                    // const nativeWorkletUrl = new URL('../worklets/native-audio-tap/native-audio-tap.worklet.js', import.meta.url).href;
                    // await this._audioContext.audioWorklet.addModule(nativeWorkletUrl);


                    // Активируем контекст
                    await this._audioContext.resume();
                }

                return this._audioContext;
            },

        // Media Session
            async createMediaSession() {
                if (this._mediaSession) {
                    return this._mediaSession;
                }


                // 1. Создаем «немой» <audio>
                const silentAudio = new Audio();
                silentAudio.src = generateSilentWav();
                silentAudio.loop = true;
                silentAudio.volume = 1;

                if (!navigator.mediaSession) {
                    return;
                }

                // Play
                navigator.mediaSession.setActionHandler('play', async () => {
                    if (!this.panes.default) {
                        return;
                    }

                    await this.panes.default.resume();
                });

                // Pause
                navigator.mediaSession.setActionHandler('pause', async () => {
                    if (!this.panes.default) {
                        return;
                    }

                    await this.panes.default.pause();
                });

                // Next
                navigator.mediaSession.setActionHandler('nexttrack', async () => {
                    if (!this.panes.default) {
                        return;
                    }

                    await this.panes.default.next();
                });

                navigator.mediaSession.setActionHandler('previoustrack', async () => {
                    if (!this.panes.default) {
                        return;
                    }

                    await this.panes.default.prev();
                });


                navigator.mediaSession.setActionHandler('seekbackward', (details) => {
                    if (!this.panes.default) {
                        return;
                    }

                    this.panes.default.seekbackward((details?.seekOffset || 10) * 1000)
                });

                navigator.mediaSession.setActionHandler('seekforward', (details) => {
                    if (!this.panes.default) {
                        return;
                    }

                    this.panes.default.seekforward((details?.seekOffset || 10) * 1000)
                });

                navigator.mediaSession.setActionHandler('seekto', (details) => {
                    if (!this.panes.default) {
                        return;
                    }

                    this.panes.default.seek(details.seekTime * 1000)
                    // if (details.fastSeek && 'fastSeek' in audio) {
                    //   audio.fastSeek(details.seekTime);
                    // } else {
                    //   audio.currentTime = details.seekTime;
                    // }
                });


                this._mediaSession = {
                    open: (payload) => {
                        navigator.mediaSession.metadata = new MediaMetadata(payload);

                        navigator.mediaSession.setPositionState({
                            duration: payload.duration,
                            playbackRate: 1,
                            position: 0
                        });
                    },
                    play: async() => {
                        await silentAudio.play();
                        navigator.mediaSession.playbackState = 'playing';
                    },
                    pause: async () => {
                        await silentAudio.pause();
                        navigator.mediaSession.playbackState = 'paused';
                    },
                    stop: async () => {
                        await silentAudio.pause();
                        navigator.mediaSession.playbackState = 'none';
                    },
                    position: ({ duration, playbackRate, position }) => {
                        if (position > 8 && position <= duration) {
                            navigator.mediaSession.setPositionState({
                                duration,
                                playbackRate: 1,
                                position
                            });
                        }
                    }
                }

                return this._mediaSession;
            },

        // Controls
            async play(pane = 'default') {
                const p = this.panes[pane];
                if (!p) {
                    return
                }

                const state = p._state;

                if (p && state === 'paused') {
                    p.resume();
                    return;
                }

                if (p && state === 'stopped') {
                    p.play();
                    return;
                }
            },
            async pause(pane = 'default') {
                const p = this.panes[pane];
                if (!p) {
                    return
                }

                const state = p._state;

                if (state === 'playing') {
                    p.pause();
                    return;
                }
            },

            async stop(pane = 'default') {
                const p = this.panes[pane];
                if (!p) {
                    return
                }

                p.stop();
                p._item = null; // clear current item
            },

        // Pane
            async pane(code = 'default') {
                if (!this.panes[code]) {
                    const usePane = createPaneStore(code);
                    const pane = usePane();

                    await this.createMediaSession();

                    await pane.setup({
                        audioContext: this._audioContext,
                        channelCount: 2,
                        mediaSession: code === 'default' ? this._mediaSession : null
                    });

                    this.panes[code] = pane;
                }

                return this.panes[code];
            },

        // Queue
            // Возвращает та же ли очередь играет или нет
            async sameQueue({ params, sort, options }, pane = 'default') {
                if (!this._audioContext) {
                    return;
                }

                const p = await this.pane(pane);

                if (!p) {
                    return;
                }

                const queue = p.queue();

                if (!queue || !Object.keys(queue).length) {
                    return;
                }

                // Сравним по сущности
                if (options?.type) {
                    if (!queue.type || queue.type !== options.type || 
                        !(
                            (options?.id && queue?.id === options?.id) ||
                            (options?.code && queue?.code === options?.code)
                        )
                    ) {
                        return;
                    }
                } else if (queue.type) {
                    return;
                }

                const sameObj = (a, b) => {
                    const aCount = a ? Object.keys(a)?.length : null;
                    const bCount = b ? Object.keys(b)?.length : null;

                    if (!aCount && bCount || aCount && !bCount) {
                        return false;
                    }

                    if (!aCount && !bCount) {
                        return true;
                    }

                    if (Object.keys(a).find(id => b[id] !== a[id])) {
                        return false;
                    }

                    if (Object.keys(b).find(id => b[id] !== a[id])) {
                        return false;
                    }

                    return true;
                }

                // Сравним по параметрам фильтрации
                if (!sameObj(options.params, queue.params)) {
                    return;
                }

                // Сравним по сортировке фильтрации
                if (!sameObj(options.sort, queue.sort)) {
                    return;
                }

                return true;
            },

            async trackQueue(tracks, options = {}, pane = 'default') {
                const items = (tracks || []).filter(item =>
                        item.id && item.wave?.length && api.tracks().canListen({ track: item })
                    );

                if (!items.length) {
                    return;
                }

                if (!this._audioContext) {
                    await this.createAudioContext();
                }

                const p = await this.pane(pane);

                if (!p) {
                    return;
                }

                p.queue({
                    items,
                    ...(options || {})
                });

                return p.queue();
            },

            async shuffleQueue(pane = 'default') {
                const p = await this.pane(pane);

                if (p?.shuffleQueue) {
                    p.shuffleQueue();
                }
            },

            async clearQueue(pane = 'default') {
                const p = await this.pane(pane);

                if (p?.clearQueue) {
                    p.clearQueue();
                }
            },

            async removeFromQueue(id, pane = 'default') {
                const p = await this.pane(pane);

                if (p?.removeFromQueue) {
                    p.removeFromQueue(id);
                }
            },

            async addToQueue(item, pane = 'default') {
                if (!api.tracks().canListen({ track: item })) {
                    return;
                }

                if (!this._audioContext) {
                    await this.createAudioContext();
                }

                const p = await this.pane(pane);

                if (p?.addToQueue) {
                    p.addToQueue(item);
                }
            },

            async playNext(item, pane = 'default') {
                if (!api.tracks().canListen({ track: item })) {
                    return;
                }

                if (!this._audioContext) {
                    await this.createAudioContext();
                }

                const p = await this.pane(pane);

                if (p?.playNext) {
                    p.playNext(item);
                }
            },

        // Repeat
            async repeat(mode, pane = 'default') {
                const p = await this.pane(pane);

                if (p?.repeat) {
                    return p.repeat(mode);
                }
            },

        // Track
            async track(track, pane = 'default') {
                if (!track?.id || !track?.wave) {
                    return null;
                }

                if (!api.tracks().canListen({ track })) {
                    return;
                }

                if (!this._audioContext) {
                    await this.createAudioContext();
                }

                const p = await this.pane(pane);

                const emitter = createEmitter();

                if (!await p.open(track)) {
                    return;
                }

                // Events
                    // State
                        const onStateChange = (payload) => {
                            if (payload?.item?.id !== track?.id) {
                                p.off('state', onStateChange);
                                return;
                            }

                            emitter.emit('state', payload?.state);
                        }

                        p.on('state', onStateChange);

                    // On end
                        const onEnd = (payload) => {
                            if (payload?.item?.id !== track?.id) {
                                p.off('end', onEnd);
                                return;
                            }

                            if (!p._crossfadeEnabled) {
                                emitter.emit('preloadNext');
                            }

                            emitter.emit('end');
                        }
                        p.on('end', onEnd);

                    // Preload next
                        const onPreloadNext = (payload) => {
                            if (payload?.item?.id !== track?.id) {
                                p.off('preloadNext', onPreloadNext);
                                return;
                            }

                            if (p._crossfadeEnabled) {
                                emitter.emit('preloadNext');
                            }
                        }

                        p.on('preloadNext', onPreloadNext);

                    // On end
                        const onOpen = (payload) => {
                            if (payload?.item?.id !== track?.id) {
                                p.off('open', onOpen);
                                p.off('state', onStateChange);
                                p.off('end', onEnd);
                                p.off('position', onPosition);
                                p.off('preloadNext', onPreloadNext);
                                return;
                            }

                            emitter.emit('open');
                        }
                        p.on('open', onOpen);

                    // Position
                        const onPosition = (payload) => {
                            if (payload?.item?.id !== track?.id) {
                                p.off('position', onPosition);
                                return;
                            }

                            emitter.emit('position', payload.position);
                        }
                        p.on('position', onPosition);
            },

            async tracks({ params, sort, options, autoPlay, load, loadParent, loadMore = false, shuffle = false, pane = 'default' }) {
                const loadDefault = async (skip) => {
                    let data = {
                        ...params
                    }

                    data.skip = skip;
                    data.take = 50;

                    if (sort?.code) {
                        data.sort = sort.code;
                        data.sort_dir = sort.direction;
                    }

                    if (options.getById) {
                        data = options.getById;
                    }

                    let items, total;

                    if (options.source) {
                        const result = await api[options.source.store]()[options.source.fnName](data);

                        items = result.items || [];
                        total = result.total || 0;
                    } else {
                        const result = await api.tracks().list(data);

                        items = result.items || [];
                        total = result.total || 0;
                    }

                    return {
                        items: items.filter(item =>
                            item.id && item.wave?.length && api.tracks().canListen({ track: item })
                        ),
                        total: total
                    }
                }

                // Если не тот же набор треков, создадим очередь
                if (!await this.queue.same({ params, sort, options })) {
                    // Получаем список треков
                        let tracks = options.items;
                        let payload;

                        if (!tracks) {
                            if (load) {
                                payload = await load(0);
                            } else {
                                payload = await loadDefault(0);
                            }

                            tracks = payload?.items || [];
                        } else if (tracks?.length && tracks?.length < 50 && !load && loadMore) {
                            payload = await loadDefault(tracks.length);

                            if (payload?.items?.length) {
                                tracks.splice(tracks.length - 1, 0, ...payload.items);
                            }
                        }

                        if (!tracks.length) {
                            return;
                        }

                    // Получaем release/dict/playlist/etc
                        if (loadParent && options.type) {
                            options[options.type] = await loadParent();
                        }

                    // Создаем очередь
                        const queue = await this.queue.create(tracks, {
                            ...(options || {}),

                            skip: tracks.length,
                            total: options.total || payload.total,
                            load: loadMore ? load || loadDefault : null
                        }, pane);


                        if (!queue?.items?.length) {
                            return;
                        }

                    // Перемешиваем
                    if (shuffle) {
                        const p = await this.pane(pane);

                        if (p) {
                            p.shuffleQueue()
                        }
                    }

                    // Включаем первый трек
                    if (autoPlay) {
                        await this.track(queue.items[0]);

                        const p = await this.pane(pane);
                        if (p) {
                            p.play();
                        }

                        return;
                    } else {
                        const p = await this.pane(pane);
                        if (p) {
                            p.loadMore();
                        }
                    }

                    return;
                }

                // Если очередь та же, то просто продолжаем
                const p = await this.pane(pane);
                const queue = p.queue();

                // Проверим количество передaнных треков
                    if (options.items?.length) {
                        if (options.items.length > queue.items.length) {
                            queue.items = [...options.items];
                        }
                    }

                // Т.е. для релизов и прочих
                if (autoPlay) {
                    // Если стоит на паузе
                    if (p._state === 'paused') {
                        p.resume();
                        return;
                    }

                    // Если закончилось воспроизведение
                    if (p._state === 'stopped' && queue.items?.length) {
                        await this.track(queue.items[0]);
                        p.play();
                        return;
                    }
                }

                return;
            },
    }
});


export default usePlayerStore;
